import { memo, useState } from 'react';
import { Check, ChevronDown, Eye, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ReviewSection } from '../types';
import { SpecMarkdown } from './SpecMarkdown';
import { VisualSectionEditor } from './VisualSectionEditor';

/** Same wording the other admin-gated controls use (dispatch forms, finder tables). */
export const ADMIN_ONLY = 'Admin only';
export const READ_ONLY_CLASS = 'cursor-not-allowed opacity-50';

const FLAG_COPY: Record<string, string> = {
  garbled_table: 'This table may not have been read cleanly — check it against the document.',
  figure: 'This includes text read from an image — check it against the document.',
};

/**
 * One section of the tech spec as a single Keep/Dismiss unit — the whole section
 * with its structure (tables, paragraphs) intact, rendered from its markdown. Flagged
 * sections start expanded and can be EDITED inline (fix a garbled table / mis-read figure
 * value before keeping); clean sections collapse to their heading and aren't editable.
 */
export const SectionCard = memo(function SectionCard({
  section,
  kept,
  open,
  editedMarkdown,
  onToggle,
  onDecide,
  onEdit,
  readOnly,
}: {
  section: ReviewSection;
  /** true = keep, false = dismiss, undefined = not yet decided (flagged sections). */
  kept: boolean | undefined;
  open: boolean;
  /** the reviewer's edited markdown for this section, if any (flagged only). */
  editedMarkdown?: string;
  onToggle: (order: number) => void;
  onDecide: (order: number, keep: boolean) => void;
  onEdit: (order: number, markdown: string) => void;
  /** Non-admins can read the section but not edit, keep or dismiss it (the server refuses
   *  those for them too). Expanding / collapsing still works. */
  readOnly: boolean;
}) {
  const flagged = section.flagged;
  const undecided = kept === undefined;
  const saved = editedMarkdown ?? section.markdown; // what's committed to the review state
  const isEdited = editedMarkdown !== undefined && editedMarkdown !== section.markdown;

  // The visual editor commits its serialized markdown to review state when the reviewer finishes
  // (Done / clicking away); it's applied to the kept spec when they Keep the section.
  const [editing, setEditing] = useState(false);

  return (
    <li
      className={cn(
        // `overflow-clip`, NOT `overflow-hidden`: both clip the rounded corners, but
        // `hidden` makes this card a scroll container, which makes the editor toolbar's
        // `sticky top-0` inert during page scroll. `clip` creates no scrollport.
        'overflow-clip rounded-xl border bg-white shadow-card',
        kept === false && 'opacity-60',
        flagged && undecided ? 'border-state-warning-accent' : 'border-line',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => onToggle(section.order)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-line-soft/40"
      >
        <ChevronDown
          className={cn('mt-0.5 size-4 shrink-0 text-muted transition-transform', !open && '-rotate-90')}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-navy" title={section.heading}>
            {section.heading}
            {isEdited && (
              <span className="ml-2 align-middle text-[11px] font-semibold text-electric">· edited</span>
            )}
          </div>
          {flagged ? (
            <p className="m-0 mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium text-state-warning-text">
              <Eye className="size-3.5" aria-hidden />
              {FLAG_COPY[section.flag_reason ?? ''] ?? 'Worth a closer look.'}
            </p>
          ) : (
            <p className="m-0 mt-0.5 text-[11.5px] text-muted">Read cleanly from your document.</p>
          )}
        </div>
        {!flagged && (
          <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[12px] font-bold text-state-success-text">
            <Check className="size-3.5" aria-hidden /> Found
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-line-soft px-4 pt-3 pb-1">
          {/* Any section can be corrected in place — flagged ones prompt for it; a clean
              section can still be fixed if docling mis-read something we didn't flag. */}
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted">
              {isEdited
                ? 'Edited — your correction will be saved when you keep this section.'
                : flagged && !readOnly
                  ? 'Fix anything that’s wrong against the document, then keep it.'
                  : ''}
            </span>
            {!editing && (
              <button
                type="button"
                disabled={readOnly}
                title={readOnly ? ADMIN_ONLY : undefined}
                onClick={() => setEditing(true)}
                className={cn(
                  'inline-flex shrink-0 items-center gap-1 rounded border border-line px-2 py-1 text-[12px] font-semibold text-navy',
                  readOnly ? READ_ONLY_CLASS : 'hover:border-electric hover:text-electric',
                )}
              >
                <Pencil className="size-3.5" aria-hidden /> Edit
              </button>
            )}
          </div>
          {editing ? (
            <VisualSectionEditor
              value={saved}
              autoFocus
              onCommit={(md) => onEdit(section.order, md)}
              onDone={() => setEditing(false)}
            />
          ) : (
            <SpecMarkdown markdown={saved} />
          )}
        </div>
      )}

      <div className="flex items-center gap-2 border-t border-line-soft bg-line-soft/30 px-4 py-2.5">
        <span className="flex-1 text-[11.5px]">
          {kept === true ? (
            <span className="inline-flex items-center gap-1 font-semibold text-state-success-text">
              <Check className="size-3.5" aria-hidden /> Kept &mdash; will be saved
            </span>
          ) : kept === false ? (
            <span className="text-muted">Dismissed &mdash; won&rsquo;t be saved</span>
          ) : (
            <span className="font-medium text-state-warning-text">Keep this section, or dismiss it.</span>
          )}
        </span>
        <button
          type="button"
          disabled={readOnly}
          title={readOnly ? ADMIN_ONLY : undefined}
          onClick={() => onDecide(section.order, false)}
          className={cn(
            'rounded border px-3 py-1 text-[12px] font-semibold',
            kept === false
              ? 'border-navy bg-navy text-white'
              : cn('border-line text-muted', !readOnly && 'hover:border-navy hover:text-navy'),
            readOnly && READ_ONLY_CLASS,
          )}
        >
          Dismiss
        </button>
        <button
          type="button"
          disabled={readOnly}
          title={readOnly ? ADMIN_ONLY : undefined}
          onClick={() => onDecide(section.order, true)}
          className={cn(
            'rounded border px-3 py-1 text-[12px] font-semibold',
            kept === true
              ? 'border-state-success-accent bg-state-success-accent text-white'
              : cn('border-state-success-accent text-state-success-text', !readOnly && 'hover:bg-state-success-bg'),
            readOnly && READ_ONLY_CLASS,
          )}
        >
          Keep section
        </button>
      </div>
    </li>
  );
});
